import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import {
  useModals,
  Avatar,
  Button,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Title,
} from "@skailer-com/uikit";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { BlogChannelEditModal } from "@/pages/blog/index/widgets/BlogChannelEditModal.tsx";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import {
  useBlogMyChannels,
  useBlogUpdateMyChannels,
  useMyChannelDetails,
} from "@/queries/blog.query.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface BlogChannelSummaryProps {
  channelId: number;
}

export function BlogChannelSummary({ channelId }: BlogChannelSummaryProps) {
  const { data: channel, isLoading } = useMyChannelDetails(channelId);
  const { mutateAsync: updateMyChannels, isPending } = useBlogUpdateMyChannels();
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { data: channels } = useBlogMyChannels();
  const { canIEdit } = usePermissions();

  const handleUpdateMyChannel = useCallback(async () => {
    const channelIds =
      channels?.reduce((acc: number[], channel) => {
        if (channelId == channel.id) {
          if (!channel.is_subscribed) acc.push(channel.id);
        } else {
          if (channel.is_subscribed) acc.push(channel.id);
        }

        return acc;
      }, []) ?? [];

    await updateMyChannels(channelIds);
  }, [channel, channels]);

  const handleEditChannel = async () => {
    if (!channel) return;

    openModal(BlogChannelEditModal, { channel });
  };

  const handleViewSubscribers = () => {
    if (!channel?.subscribes_info.employees) return;
    if (channel?.subscribes_info.employees.length <= 0) return;

    openModal(PreviewEmployeesModal, {
      filter: {
        page_number: 1,
        employee_ids: channel?.subscribes_info.employees.map((employee) => employee.id),
      },
      addColumns: [],
      modalTitle: t("core:employees"),
    });
  };

  return (
    <Stack className={"bg-light rounded-sm shadow p-6"} gap={"md"}>
      {isLoading && (
        <>
          <Stack direction={"horizontal"} gap={"smd"} items={"center"}>
            <Skeleton className="w-16 h-16 rounded-sm" />
            <Stack gap={"sm"}>
              <Skeleton className="w-20 h-5 rounded-sm" />
              <Skeleton className="w-20 h-5 rounded-sm" />
            </Stack>
          </Stack>
          <Stack items={"center"} direction={"horizontal"} gap={"md"}>
            <Skeleton className={"h-7 w-20"} />
          </Stack>
        </>
      )}
      {channel && (
        <>
          <Stack direction={"horizontal"} gap={"smd"} items={"center"}>
            <Avatar size={"xl"} emoji={channel.emoji} />
            <Stack>
              <Title header={channel.title} size={"lg"} caption={channel.description} />
            </Stack>
          </Stack>
          <Stack items={"center"} direction={"horizontal"} gap={"md"}>
            <Stack direction={"horizontal"} gap={"sm"}>
              {canIEdit(ApiPermission.news_manage_channels) && (
                <Button size={"md"} onClick={handleEditChannel} variant={"secondary"}>
                  {t("common:edit")}
                </Button>
              )}
              <Button
                variant={channel.is_subscribed ? "secondary" : "primary"}
                size={"md"}
                onClick={handleUpdateMyChannel}
                isLoading={isPending}
                leftIcon={
                  channel.is_subscribed ? <FontAwesomeIcon icon="fa-light fa-check" /> : undefined
                }
              >
                {channel.is_subscribed ? t("blog:subscribed") : t("blog:subscribe")}
              </Button>
            </Stack>
            <Stack
              direction={"horizontal"}
              gap={"sm"}
              className={twMerge(
                "text-secondary",
                channel.subscribes_info.employees && channel.subscribes_info.employees.length > 0
                  ? " hover:text-dark/70 cursor-pointer"
                  : ""
              )}
              onClick={handleViewSubscribers}
            >
              {channel.subscribes_info.employees && channel.subscribes_info.employees.length > 0 ? (
                <Stack direction={"horizontal"} gap={"xs"}>
                  {channel.subscribes_info.employees.slice(0, 3).map((employee) => (
                    <EmployeeAvatar
                      size={"sm"}
                      employee={employee}
                      key={`subscriber-${employee.id}`}
                    />
                  ))}
                </Stack>
              ) : (
                <></>
              )}
              {t("plurals:subscribers", { count: channel.subscribes_info.count })}
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
}
